import React, { useEffect, useMemo, useState } from 'react'
import { Flex, Text, TextVariant, InfoIcon } from '@mprolab/uikit'
import MainWrapper from 'layouts/MainWrapper'
import { StyledBiennale } from 'components'
import styled, { useTheme } from 'styled-components'
import Image from 'components/CompatImage'
import { observer } from 'mobx-react-lite'
import { EmptyNodes, IndexNodesTable } from 'components/Licenses'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { indexNodeStore } from 'mobxStores/IndexNodeStore'
import { useLifetimeTokens } from 'state/global/hooks'
import {
  useGetLicensesInNetworkQuery,
  useGetLicensesQuery,
  useSyncLicensesMutation
} from 'state/services/indexNode/indexNode'
import { isEmpty } from 'lodash'
import UseAnimations from 'react-useanimations'
import loading from 'react-useanimations/lib/loading'
import isNil from 'lodash/isNil'
import { LicenseStatus, LicenseType, SessionType } from '@mprolab/sdk'
import { useVerifyIndexNode } from 'hooks/useVerifyIndexNode'
import { setLifetimeTokens } from 'state/global/actions'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'state'
import { useAwaitingWalletVerificationModal } from 'components/widgets/VerificationModals'
import { RefreshCcw } from 'react-feather'
import useGetSingleAccessToken from 'hooks/useGetSingleAccessToken'
import { logMessage } from 'utils/logMessage'
import { useTranslation } from 'react-i18next'
import useToast from 'hooks/useToast'
import { useIsUserVerified } from 'state/user/hooks'
import AppSessionRequiredPanel from 'components/AppSessionRequiredPanel'
import { getErrorCodesDescription } from 'utils/getErrorCodesDescription'
import dottedBackground from '../../public/images/dottedBackgroundPattern1.webp'

export const TableWrapper = styled(Flex)`
  gap: 8px;
  width: 100%;
  height: 100%;
  min-height: 321px;
  padding: 24px;
  flex-direction: column;
  justify-content: center;
  background: ${({ theme }) => theme.colors.backgroundContrast};
  margin: 16px 0 56px;
  ${({ theme }) => theme.mediaQueries.mobile} {
    margin-bottom: 32px;
  }
`

const ActiveLicensesWrapper = styled(Flex)`
  gap: 12px;
  margin-top: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`

const ActiveLicenseCard = styled(Flex)`
  min-width: 180px;
  flex-direction: column;
  gap: 6px;
  padding: 16px 20px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.backgroundContrast};
`

const RefreshWrapper = styled(Flex)<{ isRefreshing: boolean }>`
  cursor: pointer;
  animation: ${({ isRefreshing }) => (isRefreshing ? 'rotation' : 'none')} 1.5s infinite;
  @keyframes rotation {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(-360deg);
    }
  }
`

const StyledTitle = styled(StyledBiennale)`
  font-size: 32px;
`

const DottedBackgroundWrapper = styled(Flex)`
  position: absolute;
  top: -150px;
  left: 70px;
  width: 100%;
  height: 700px;
  z-index: -1;
  ${({ theme }) => theme.mediaQueries.tablet} {
    height: 550px;
    left: 0;
    left: 80px;
  }
`

const Licenses: React.FC = observer(() => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const { toastError } = useToast()
  const { account, provider } = useWeb3React()
  const isAppVerified = useIsUserVerified()
  const { isLoading } = indexNodeStore
  const lifetimeTokens = useLifetimeTokens()
  const dispatch = useDispatch<AppDispatch>()
  const accessToken = lifetimeTokens?.[account]
  const { obtainAccessToken } = useGetSingleAccessToken()

  const {
    data: licenses,
    isLoading: licensesLoading,
    refetch: refetchGetLicenses,
    error
  } = useGetLicensesQuery({ account, accessToken }, { skip: isNil(accessToken) || !isAppVerified })
  const {
    data: nodesInNetwork,
    isLoading: nodesInNetworkLoading,
    refetch: refetchNodesInNetwork
  } = useGetLicensesInNetworkQuery({ account, accessToken }, { skip: isNil(accessToken) || !isAppVerified })
  const verifyIndexNode = useVerifyIndexNode()
  const { onPresentAwaitingVerificationModal, onModalClose } = useAwaitingWalletVerificationModal({})
  const loadingTable = isLoading || licensesLoading
  const [syncLicenses] = useSyncLicensesMutation()

  const refetchLicenses = async () => {
    await refetchGetLicenses()
    await refetchNodesInNetwork()
  }

  // ----- Refresh logic ----- //
  const [refreshLicenses, setRefreshLicenses] = useState(false)
  const activeLicenseCounts = useMemo(() => {
    const activeLicenses = (licenses || []).filter((license) => license.status !== LicenseStatus.TERMINATED)

    return {
      iNode: activeLicenses.filter((license) => license.type === LicenseType.MGA_INODE).length,
      ggNode: activeLicenses.filter((license) => license.type === LicenseType.MGA_LIGHT).length
    }
  }, [licenses])
  const networkLicenseCounts = useMemo(() => {
    const counts = (nodesInNetwork || []).reduce(
      (acc, license) => {
        if (license.type === LicenseType.MGA_INODE) {
          acc.iNode = license.count
        }

        if (license.type === LicenseType.MGA_LIGHT) {
          acc.ggNode = license.count
        }

        return acc
      },
      { iNode: 0, ggNode: 0 }
    )

    return counts
  }, [nodesInNetwork])

  const handleRefresh = async () => {
    setRefreshLicenses(true)
    onPresentAwaitingVerificationModal()
    await obtainAccessToken(
      async (accessToken) => {
        try {
          await syncLicenses({ account, accessToken })
          await refetchLicenses()
        } catch (error) {
          logMessage(error)
        }
      },
      (err) => {
        toastError(getErrorCodesDescription(t, err))
      }
    )
    onModalClose()
    setRefreshLicenses(false)
  }

  const getIndexNodeContent = () => {
    switch (true) {
      case loadingTable:
        return (
          <Flex justifyContent="center" alignItems="center" flexGrow={1}>
            <UseAnimations animation={loading} strokeColor={colors.white3} size={64} />
          </Flex>
        )
      case isEmpty(licenses):
        /*
         * Which of the two emptinesses this is.
         *
         * Without a session the app cannot see anybody's licences, and saying
         * "you have none" in that state is a claim about their property that
         * we are in no position to make.
         */
        return (
          <EmptyNodes
            reason={isNil(accessToken) ? 'not-signed-in' : 'none'}
            onSignIn={() => void verifyAndSetToken()}
          />
        )
      default:
        return <IndexNodesTable {...{ licenses, refetchLicenses }} />
    }
  }

  const verifyAndSetToken = async () => {
    /*
     * Nothing is attempted until the wallet can actually sign.
     *
     * `account` and `provider` do not arrive together: the address is
     * synchronous, the provider is set by an async effect awaiting
     * `connector.getProvider()`. Without this guard the effect below fired the
     * moment the address appeared, `verifyIndexNode` returned "Wallet account
     * or provider missing" immediately, and the user got an error toast on
     * first entry that went away on refresh — because by then the provider was
     * there. NodesStorage already had this guard; these two pages did not.
     */
    if (!account || !provider) return
    indexNodeStore.setIsLoading(true)
    onPresentAwaitingVerificationModal()
    await verifyIndexNode({
      account,
      provider,
      successCallback: async (accessToken) => {
        dispatch(setLifetimeTokens({ account, accessToken }))
        onModalClose()
        indexNodeStore.setIsLoading(false)
      },
      errorCallback: async () => {
        toastError(t('protectedPages.indexNodeSessionFailed'))
        onModalClose()
        indexNodeStore.setIsLoading(false)
      },
      sessionType: SessionType.LIFETIME
    })
  }

  // Index-node session only after app wallet-auth; never redirect home on failure (user stays on this page).
  useEffect(() => {
    if (!account || !provider || !isAppVerified) return
    // @ts-ignore
    if (isNil(accessToken) || error?.data?.message === 'Invalid session: Session not found') {
      void verifyAndSetToken()
    }
  }, [account, provider, isAppVerified, accessToken, error])

  return (
    <MainWrapper>
      <Flex fullWidth mt="80px" mb="234px" flexDirection="column">
        <Flex mt="72px" mb="32px" justifyContent="space-between" alignItems="center">
          <StyledTitle>{t('nodes.licenses')}</StyledTitle>
        </Flex>
        <Flex width="100%" flexDirection="column">
          {!loadingTable && !nodesInNetworkLoading && !isEmpty(licenses) && (
            <ActiveLicensesWrapper>
              {/*
                Mine first, then the network — interleaving them read as one
                number contradicting the next: a holder with no ggNODE saw a
                "0" sitting beside "54 in network" and took it for a fault.
              */}
              <ActiveLicenseCard>
                <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
                  My active iNODE
                </Text>
                <Text variant={TextVariant.H4}>{activeLicenseCounts.iNode}</Text>
              </ActiveLicenseCard>
              <ActiveLicenseCard>
                <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
                  My active ggNODE
                </Text>
                <Text variant={TextVariant.H4}>{activeLicenseCounts.ggNode}</Text>
              </ActiveLicenseCard>
              <ActiveLicenseCard>
                <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
                  iNODE total in network
                </Text>
                <Text variant={TextVariant.H4}>{networkLicenseCounts.iNode}</Text>
              </ActiveLicenseCard>
              <ActiveLicenseCard>
                <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
                  ggNODE total in network
                </Text>
                <Text variant={TextVariant.H4}>{networkLicenseCounts.ggNode}</Text>
              </ActiveLicenseCard>
            </ActiveLicensesWrapper>
          )}
          <TableWrapper>
            {account && !isAppVerified ? (
              <AppSessionRequiredPanel />
            ) : (
              <>
                {!loadingTable && (
                  <Flex fullWidth justifyContent="flex-end" alignItems="center" gap="10px">
                    <RefreshWrapper isRefreshing={refreshLicenses} onClick={handleRefresh}>
                      <RefreshCcw color={colors.gray6} size={20} />
                    </RefreshWrapper>
                    {/*
                      A licence bought a moment ago is on chain before it is in
                      this list: ownership moves when the transfer indexer sees
                      the event, once a minute. Without saying so, a refresh
                      that shows nothing reads as a lost licence.
                    */}
                    <InfoIcon
                      iconProps={{ size: 16 }}
                      tooltipProps={{
                        title: t('licenses.indexingInfo'),
                        placement: 'left'
                      }}
                    />
                  </Flex>
                )}
                {getIndexNodeContent()}
              </>
            )}
          </TableWrapper>
        </Flex>
      </Flex>
      <DottedBackgroundWrapper>
        <Image src={dottedBackground} objectFit="cover" loading="eager" />
      </DottedBackgroundWrapper>
    </MainWrapper>
  )
})

export default Licenses
